import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { getFetchOptions } from './useAPI.helper';

import {
  ApiPath,
  ApiRequestPayload,
  ApiResponsePayload,
  TUseAPI,
} from './useAPI.types';

export function useAPI(): TUseAPI {
  const navigate = useNavigate();

  const callAPI = useCallback(
    (
      path: ApiPath,
      payload: ApiRequestPayload,
      method?: 'POST' | 'GET',
    ): Promise<ApiResponsePayload> => {
      const query = method === 'GET' ? `?${new URLSearchParams(payload).toString()}` : '';

      return fetch(
        `/${path}${query}`,
        getFetchOptions(payload, method),
      )
        .then((response) => {
          if (response.redirected) {
            const { pathname, origin, href } = new URL(response.url);

            return {
              redirect: origin.includes('app.')
                ? (): void => { window.location.href = href; }
                : (): void => navigate(pathname),
            };
          }

          return response.json()
            .then((data) => {
              if (!response.ok) {
                return new Error(data.message);
              }
              // Backend may opt into JSON-mode redirect (200 + redirect_url)
              // instead of a 302, so the browser top-level-navigates rather
              // than fetch-following a cross-origin OAuth callback.
              if (data && typeof data.redirect_url === 'string') {
                const url: string = data.redirect_url;
                // /authorize is a Go OAuth handler — needs a real HTTP
                // request. Same for absolute URLs. Everything else is a
                // React route; soft-nav preserves sessionStore (e.g. 2FA
                // depends on confirmAccountID surviving the redirect).
                const needsHardNav = url === '/authorize' || /^https?:\/\//.test(url);
                return {
                  redirect: needsHardNav
                    ? (): void => { window.location.href = url; }
                    : (): void => navigate(url),
                };
              }
              return data;
            })
            .catch(() => new Error(response.statusText));
        })
        .catch(({ message }) => new Error(message));
    },
    [navigate],
  );

  return { callAPI };
}
